import { useSyncExternalStore } from 'react';

export interface IOrbitL4TurnStatus {
  /** A request for this turn is out, or its confirm was accepted and the card stays locked. */
  busy: boolean;
  /** Set by a 410 from confirm: the card turns Expired with this text, `null` for the default. */
  expiredMessage?: string | null;
  /** The last request failed for a reason other than expiry; the buttons come back. */
  failed?: boolean;
  /** Cancel was pressed. There is no cancel endpoint, so this only hides the buttons here. */
  dismissed?: boolean;
  /** The builder saved a record that Orbit could not be told about (a 409, or the call failed). */
  savedRecord?: { entity: string; id: number };
  /** The builder is open over this card: buttons disabled, no working line. */
  builderOpen?: boolean;
  /** The builder already opened by itself for this turn, so a re-render never opens it again. */
  autoOpened?: boolean;
}

const IDLE: IOrbitL4TurnStatus = { busy: false };

/*
  Module scope, not component state: the panel body unmounts on close and remounts on open, and a
  confirm still in flight across that must keep its card locked.
*/
let statuses: ReadonlyMap<number, IOrbitL4TurnStatus> = new Map();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const orbitL4Status = (turnId: number): IOrbitL4TurnStatus => statuses.get(turnId) ?? IDLE;

export const setOrbitL4Status = (turnId: number, status: IOrbitL4TurnStatus): void => {
  const next = new Map(statuses);
  next.set(turnId, status);
  statuses = next;
  listeners.forEach(listener => listener());
};

export const resetOrbitL4Statuses = (): void => {
  statuses = new Map();
  listeners.forEach(listener => listener());
};

export const useOrbitL4Status = (turnId: number): IOrbitL4TurnStatus =>
  useSyncExternalStore(subscribe, () => orbitL4Status(turnId));
